import { useEffect, useRef, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth.js';
import { isSuperadmin } from '../utils/roles.js';
import { api, type ProfileSummary } from '../api.js';
import { useActiveProfileId } from '../active-profile.js';
import { Icon } from './Icon.js';
import { isolate } from '../utils/bidi.js';

interface Props {
  collapsed: boolean;
}

export function ProfileSwitcher({ collapsed }: Props): JSX.Element | null {
  const [profiles, setProfiles] = useState<ProfileSummary[] | null>(null);
  const [activeId, setActiveId] = useActiveProfileId();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const nav = useNavigate();
  // Создание профиля — здесь же, суперадмину: пункт «+ Профиль» в списке.
  const canCreate = isSuperadmin(user?.role);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [createBusy, setCreateBusy] = useState(false);

  const create = async (): Promise<void> => {
    const name = newName.trim();
    if (!name) return;
    setCreateBusy(true);
    setCreateErr(null);
    try {
      const created = await api.createProfile({ name });
      window.dispatchEvent(new Event('profiles-changed'));
      setActiveId(created.id);
      setCreating(false);
      setNewName('');
      setOpen(false);
      nav('/admin/settings');
    } catch (e) {
      setCreateErr((e as Error).message);
    } finally {
      setCreateBusy(false);
    }
  };

  const fetchProfiles = (): void => {
    api
      .listProfiles()
      .then((list) => {
        const summaries = list.map((p) => ({ id: p.id, name: p.name, isActive: p.isActive }));
        // Суперадмин и админ видят и выключенные профили: иначе выключенный
        // в «Настройках» профиль было бы негде включить обратно. Менеджеру
        // сервер и так отдаёт только активные.
        setProfiles(summaries);
        const preferred = summaries.find((p) => p.isActive) ?? summaries[0];
        if (!activeId && preferred) setActiveId(preferred.id);
        if (activeId && !summaries.find((p) => p.id === activeId) && preferred) {
          setActiveId(preferred.id);
        }
      })
      .catch(() => setProfiles([]));
  };

  useEffect(() => {
    fetchProfiles();
    window.addEventListener('profiles-changed', fetchProfiles);
    return () => window.removeEventListener('profiles-changed', fetchProfiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setCreating(false);
        setCreateErr(null);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!profiles) return null;
  // Профилей нет: переключать нечего, но суперадмину нужен «+ Профиль»,
  // иначе первый профиль из интерфейса не создать.
  if (profiles.length === 0 && !canCreate) return null;
  const current = profiles.find((p) => p.id === activeId) ?? profiles[0] ?? null;
  const initial = current?.name[0]?.toUpperCase() ?? '+';

  if (collapsed) {
    return (
      <div ref={ref} style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          title={current ? `Профиль: ${isolate(current.name)}` : 'Профиль не выбран'}
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'var(--bg-subtle)',
            border: '1px solid var(--hairline)',
            color: 'var(--fg)',
            display: 'grid',
            placeItems: 'center',
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {initial}
        </button>
        {open && (
          <Menu
            profiles={profiles}
            activeId={current?.id ?? null}
            onPick={pick}
            placement="collapsed"
            create={canCreate ? { creating, name: newName, err: createErr, busy: createBusy, setCreating, setName: setNewName, submit: create } : null}
          />
        )}
      </div>
    );
  }

  function pick(id: string): void {
    setActiveId(id);
    setOpen(false);
  }

  return (
    <div ref={ref} style={{ position: 'relative', marginBottom: 4 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          background: 'var(--bg-subtle)',
          border: '1px solid var(--hairline)',
          borderRadius: 'var(--radius)',
          color: 'var(--fg)',
          fontSize: 12.5,
          fontWeight: 500,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            fontSize: 10,
            color: 'var(--fg-subtle)',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
          }}
        >
          Профиль
        </span>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {current ? <bdi>{current.name}</bdi> : 'нет профилей'}
          {current && !current.isActive && (
            <span style={{ color: 'var(--fg-subtle)', fontWeight: 400 }}> · выкл.</span>
          )}
        </span>
        <Icon name="chevron" size={11} />
      </button>
      {open && (
        <Menu
          profiles={profiles}
          activeId={current?.id ?? null}
          onPick={pick}
          placement="expanded"
          create={canCreate ? { creating, name: newName, err: createErr, busy: createBusy, setCreating, setName: setNewName, submit: create } : null}
        />
      )}
    </div>
  );
}

interface CreateCtl {
  creating: boolean;
  name: string;
  err: string | null;
  busy: boolean;
  setCreating: (v: boolean) => void;
  setName: (v: string) => void;
  submit: () => Promise<void>;
}

function Menu({
  profiles,
  activeId,
  onPick,
  placement,
  create,
}: {
  profiles: ProfileSummary[];
  activeId: string | null;
  onPick: (id: string) => void;
  placement: 'expanded' | 'collapsed';
  /** null — у этой роли создания нет. */
  create: CreateCtl | null;
}): JSX.Element {
  return (
    <div
      style={{
        position: 'absolute',
        top: '100%',
        left: placement === 'collapsed' ? 30 : 0,
        right: placement === 'expanded' ? 0 : undefined,
        marginTop: 4,
        background: 'var(--bg)',
        border: '1px solid var(--hairline)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow-lg, 0 4px 16px rgba(0,0,0,0.12))',
        padding: 4,
        zIndex: 50,
        minWidth: placement === 'collapsed' ? 200 : '100%',
      }}
    >
      {profiles.map((p) => (
        <button
          type="button"
          key={p.id}
          onClick={() => onPick(p.id)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 10px',
            background: p.id === activeId ? 'var(--bg-sunken)' : 'transparent',
            border: 'none',
            borderRadius: 'calc(var(--radius) - 2px)',
            color: 'var(--fg)',
            fontSize: 13,
            fontWeight: p.id === activeId ? 500 : 400,
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <bdi>{p.name}</bdi>
            {!p.isActive && <span style={{ color: 'var(--fg-subtle)' }}> · выкл.</span>}
          </span>
          {p.id === activeId && <Icon name="check" size={12} />}
        </button>
      ))}
      {create && (
        <div style={{ borderTop: '1px solid var(--hairline)', marginTop: 4, paddingTop: 4 }}>
          {create.creating ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void create.submit();
              }}
              style={{ display: 'grid', gap: 6, padding: '4px 6px 6px' }}
            >
              <input
                className="input"
                autoFocus
                placeholder="Название профиля"
                value={create.name}
                onChange={(e) => create.setName(e.target.value)}
                style={{ height: 32, fontSize: 13 }}
              />
              {create.err && (
                <div style={{ fontSize: 11.5, color: 'var(--danger-fg)' }}>{create.err}</div>
              )}
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={create.busy || !create.name.trim()}
                >
                  Создать
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  onClick={() => {
                    create.setCreating(false);
                    create.setName('');
                  }}
                >
                  Отмена
                </button>
              </div>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => create.setCreating(true)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                background: 'transparent',
                border: 'none',
                borderRadius: 'calc(var(--radius) - 2px)',
                color: 'var(--fg-muted)',
                fontSize: 13,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <Icon name="plus" size={12} />
              <span>Профиль</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
